import React, { useRef, useEffect, FunctionComponent } from 'react'
import { TabsProvider, TabsProviderProps, useTabs } from './context'
import { TabComponent, TabIndicator, TabItem, TabList, TabPanel } from './styles'
import { useWrapButtonFocus } from './useWrapButtonFocus'
import { message } from '~/services'

interface TabItemComponentProps {
  children: string | JSX.Element
  id: string
  selectItem: (id: string) => void
}

const TabItemComponent = ({ children, id, selectItem }: TabItemComponentProps): JSX.Element => {
  const { tabSelected } = useTabs()

  return (
    <TabItem tabItemId={id} isSelected={tabSelected === id} onClick={() => selectItem(id)} tabIndex={-1}>
      {children}
    </TabItem>
  )
}

// ------------------------------------------------------------------------------

interface TabOptionsComponentProps {
  children: JSX.Element[] | JSX.Element
}

const TabOptionsComponent = ({ children }: TabOptionsComponentProps): JSX.Element => {
  const { changeTabSelected, tabListRef, tabSelected } = useTabs()
  const { keyboardShortcuts, forceFocusElement } = useWrapButtonFocus(tabListRef)
  const tabIndicatorRef = useRef<HTMLDivElement>(null)

  const getAllTabs = (fatherContainer: HTMLDivElement): HTMLButtonElement[] => {
    return Array.from(fatherContainer.querySelectorAll('button'))
  }

  useEffect(() => {
    if (tabListRef.current && tabIndicatorRef.current) {
      const tabs = getAllTabs(tabListRef.current)
      const initialTabSelectedIndex = Number(tabSelected.replace('tab-', ''))

      tabIndicatorRef.current.style.width = `${tabs[initialTabSelectedIndex].offsetWidth}px`
      tabIndicatorRef.current.style.transform = `translateX(${tabs[initialTabSelectedIndex].offsetLeft}px)`

      tabs[initialTabSelectedIndex].setAttribute('tabindex', '0')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabListRef, children])

  const handleSelectTab = (tabItemId: string): void => {
    if (tabListRef.current && tabIndicatorRef.current) {
      const tabs = getAllTabs(tabListRef.current)
      const newTabSelected = tabs.find(tab => tab.id === tabItemId)

      if (newTabSelected) {
        tabIndicatorRef.current.style.width = `${newTabSelected.offsetWidth}px`
        tabIndicatorRef.current.style.transform = `translateX(${newTabSelected.offsetLeft}px)`

        forceFocusElement(tabItemId)
        changeTabSelected(tabItemId)
      }
    }
  }

  const withTabItemProps = (tabItem: React.ReactElement<TabItemComponentProps>, index: number) => {
    const tabIdentifier = `tab-${index}`

    return React.cloneElement(tabItem, {
      ...tabItem.props,
      selectItem: handleSelectTab,
      id: tabIdentifier
    })
  }

  return (
    <TabList onKeyDown={keyboardShortcuts} ref={tabListRef} label={message('analyticalModels.analytical_models')}>
      {React.Children.map(children, (child, index) => {
        return withTabItemProps(child, index)
      })}

      <TabIndicator ref={tabIndicatorRef}></TabIndicator>
    </TabList>
  )
}

// ------------------------------------------------------------------------------

interface TabContentComponentProps extends React.CSSProperties {
  children: JSX.Element[] | JSX.Element
}

const TabContentComponent = ({ children, ...rest }: TabContentComponentProps): JSX.Element => {
  const { tabSelected } = useTabs()

  return (
    <>
      {React.Children.map(children, (child, index) => {
        const tabIdentifier = `tab-${index}`

        if (tabSelected === tabIdentifier) {
          return (
            <TabPanel key={index} tabItemId={tabIdentifier} style={{ ...rest }}>
              {child}
            </TabPanel>
          )
        }

        return null
      })}
    </>
  )
}

// ------------------------------------------------------------------------------

export interface TabsComponentProps extends TabsProviderProps {
  children: JSX.Element[]
}

interface ITabsComposition {
  Item: FunctionComponent<Pick<TabItemComponentProps, 'children'>>
  Options: FunctionComponent<TabOptionsComponentProps>
  Content: FunctionComponent<TabContentComponentProps>
}

const Tabs: FunctionComponent<TabsComponentProps> & ITabsComposition = ({ children, ...rest }) => {
  return (
    <TabsProvider {...rest}>
      <TabComponent>{children}</TabComponent>
    </TabsProvider>
  )
}

Tabs.Item = TabItemComponent as FunctionComponent<Pick<TabItemComponentProps, 'children'>>
Tabs.Options = TabOptionsComponent
Tabs.Content = TabContentComponent

export { Tabs }
